import { cn } from "@/lib/utils";
import { Fragment, useMemo } from "react";
import type { CSSProperties, ReactNode } from "react";
import { Navigate, Outlet, Route, useMatch, useParams } from "react-router";
import { useFigures } from "@/hooks/use-figures";
import { useLatestRunState } from "@/hooks/use-generate";
import { useNotifications } from "@/hooks/use-notifications";
import { KeyedV2Draft } from "./KeyedV2Draft";
import { DraftOpening } from "./DraftOpening";
import { useQuestions } from "@/hooks/use-questions";
import { noveltyTabCounts, railCounts } from "../notifications/cadence";
import { useInventionElements } from "@/hooks/use-invention";
import { useNoveltyGateFor } from "@/hooks/use-novelty-gate";
import { useTask, useTaskList } from "@/hooks/use-tasks";
import { useRecordLastStage } from "./record-last-stage";
import { STAGE_TABS_HEIGHT, StageTabs } from "./StageTabs";
import { ToastSurface } from "@/components/ui/ToastSurface";
import {
  TerminologyContext,
  TerminologyDock,
  TerminologyToggle,
  TerminologyTrip,
  terminologyPlace,
  useTerminology,
} from "../terms/Terminology";
import { stepsDone } from "../stages/figure-steps";
import { resolveDraftTitle } from "./draft-title";
import { SHELL_HEADER_HEIGHT, V2Header } from "./Header";
import type { Crumb } from "./Header";
import { useClientFromList } from "@/hooks/use-clients";
import type {
  ClientSummary,
  TaskResponse,
  TaskSummary,
} from "@patent-studio/api-types";
import {
  CLIENTS_PATH,
  UNASSIGNED_PATH,
  UNASSIGNED_TITLE,
  clientHubPath,
  clientSwitcher,
} from "../clients/paths";
import { ShellFoot } from "./DisclaimerFooter";
import { V2LegalPanel } from "./LegalPanel";
import { V2StageRail } from "./StageRail";
import { OpenQuestionsPage } from "../questions/OpenQuestionsPage";
import { unresolved } from "../questions/asked-at";
import { GenerateReadyPane } from "../generate/GenerateReadyPane";
import { GenerateReviewPane } from "../stages/GenerateReviewPane";
import { ClaimStrategyStagePane } from "../stages/ClaimStrategyStagePane";
import { FiguresDefinePane } from "../stages/FiguresDefinePane";
import { FiguresReviewPane } from "../stages/FiguresReviewPane";
import { FiguresStagePane } from "../stages/FiguresStagePane";
import { IntakeStagePane } from "../stages/IntakeStagePane";
import { ClassificationStagePane } from "../stages/ClassificationStagePane";
import { InventionStagePane } from "../stages/InventionStagePane";
import { NoveltyGuard } from "../novelty/NoveltyGuard";
import { reachableTabs } from "../novelty/tab-reachability";
import { NoveltyPriorArtPane } from "../novelty/NoveltyPriorArtPane";
import { DefinePositionsPane } from "../novelty/positions/DefinePositionsPane";
import { ClaimStrategyGuard } from "../claim-strategy/ClaimStrategyGuard";
import { useClaimStrategy } from "@/hooks/use-claim-strategy";
import { useStageGate } from "@/hooks/use-stage-gate";
import { shutStages } from "../stage-gate/gated-stages";
import { StageGateGuard } from "../stage-gate/StageGateGuard";
import { NoveltyAnalysisPane } from "../novelty/analysis/NoveltyAnalysisPane";
import { PlanningOutlineStagePane } from "../stages/PlanningOutlineStagePane";
import {
  FIGURE_SUB_STEPS,
  draftStepLabel,
  findStage,
  draftOpeningPath,
  stagePath,
  stepDestination,
  subStepsOf,
  tabDestination,
} from "@/v2/stage-vocabulary";
import type {
  FigureSubStepId,
  PageTab,
  SubStep,
  SubStepId,
  TabGroup,
  TabId,
  V2StageId,
} from "@/v2/stage-vocabulary";

/**
 * The header's trail over an open draft: the attorney's clients, the draft's
 * client (or the drafts under none), and the draft. The client's name comes off
 * the list the landing page holds; until it arrives that step is left out
 * rather than drawn as a placeholder.
 *
 * The client's step switches to another of the caller's clients, and the
 * draft's to another draft beside it, each opening where a draft opens from its
 * row.
 */
export function draftCrumbs(
  task: TaskResponse | undefined,
  client: ClientSummary | undefined,
  around: {
    taskId?: string;
    clients?: readonly ClientSummary[];
    drafts?: readonly TaskSummary[];
  } = {},
): Crumb[] {
  const crumbs: Crumb[] = [{ label: "Your clients", to: CLIENTS_PATH }];
  if (task?.client_id === null) {
    crumbs.push({ label: UNASSIGNED_TITLE, to: UNASSIGNED_PATH });
  } else if (client !== undefined) {
    crumbs.push({
      label: client.name,
      to: clientHubPath(client.client_id),
      switcher: clientSwitcher(around.clients ?? [], client.client_id),
    });
  }
  const { taskId, drafts = [] } = around;
  crumbs.push({
    label: resolveDraftTitle(task),
    switcher: taskId === undefined ? undefined : draftSwitcher(taskId, drafts),
  });
  return crumbs;
}

/**
 * The drafts beside the open one, each keyed by where it opens, as its row and
 * the palette open it. The open draft is keyed the same way, off the same list,
 * so it is the one ticked.
 */
function draftSwitcher(taskId: string, drafts: readonly TaskSummary[]) {
  const open = drafts.find((draft) => draft.task_id === taskId);
  return {
    name: "Switch draft",
    choices: drafts.map((draft) => ({
      key: draftOpeningPath(draft),
      label: resolveDraftTitle(draft),
    })),
    current:
      open === undefined ? stagePath(taskId, "intake") : draftOpeningPath(open),
  };
}

/** Every draft a switch lists, rather than the rail's few most recent. */
const SWITCH_LIMIT = 100;

/** Resolves the open draft and its client for the header's trail. */
function ConnectedV2Header({ terminology }: { terminology: ReactNode }) {
  const { taskId } = useParams<{ taskId: string }>();
  const { data: task } = useTask(taskId ?? null);
  const clients = useClientFromList(task?.client_id);
  const drafts = useTaskList(
    task?.client_id
      ? { client_id: task.client_id, limit: SWITCH_LIMIT }
      : { without_client: true, limit: SWITCH_LIMIT },
    { enabled: task !== undefined },
  );

  return (
    <V2Header
      crumbs={draftCrumbs(task, clients.client, {
        taskId,
        clients: clients.data?.clients,
        drafts: drafts.data?.items,
      })}
      terminology={terminology}
    />
  );
}

/**
 * The figure stage's steps as a strip, with each step's done mark read from the
 * one rule for it. Its own component because only the figure engine has a done
 * rule, and it reads the figures query, which no other strip has any business
 * fetching.
 */
function ConnectedFigureStageTabs({
  taskId,
  activeId,
}: {
  taskId: string;
  activeId: FigureSubStepId;
}) {
  const { data } = useFigures(taskId);
  // Memoised because this sits in the shell's render body and the projection walks
  // every figure and registry row.
  const done = useMemo(() => stepsDone(data), [data]);
  return (
    <StageTabs
      label="Figure engine steps"
      entries={FIGURE_SUB_STEPS}
      activeId={activeId}
      done={done}
      hrefFor={(step) => stepDestination(step, taskId, "figures")}
    />
  );
}

/** Where a page's strip is drawn, and which of its tabs is open. */
interface PageTabsProps {
  taskId: string;
  stageId: V2StageId;
  step: SubStep;
  group: TabGroup;
  active: PageTab;
}

/** A page's tabs as a strip. Nothing counts a tab done yet, so every one keeps its number. */
function PageTabs({
  taskId,
  stageId,
  step,
  group,
  active,
  reachable,
  counts,
}: PageTabsProps & {
  /** Which tabs this matter may walk onto, where something decides that. */
  reachable?: Readonly<Partial<Record<TabId, boolean>>>;
  /** The changes waiting on each tab. */
  counts?: Readonly<Partial<Record<TabId, number>>>;
}) {
  return (
    <StageTabs
      label={group.label}
      entries={group.tabs}
      activeId={active.id}
      done={{}}
      counts={counts}
      hrefFor={(tab) =>
        // The tab being read keeps its address whatever the gate says. A page
        // does not become unreachable while the attorney is standing on it, and
        // withholding the address would drop the current marker with it.
        tab.id !== active.id && reachable?.[tab.id] === false
          ? undefined
          : tabDestination(tab, taskId, stageId, step.id)
      }
    />
  );
}

/**
 * The novelty page's strip, narrowed by the gate the server projects.
 *
 * Its own component, and mounted on that page alone, since only that page's
 * tabs are gated. A page whose tabs nothing gates renders the strip directly.
 */
function ConnectedNoveltyTabs(props: PageTabsProps) {
  const { data: gate } = useNoveltyGateFor(props.taskId);
  const { data: changes } = useNotifications(props.taskId);
  return (
    <PageTabs
      {...props}
      reachable={reachableTabs(gate)}
      counts={noveltyTabCounts(changes)}
    />
  );
}

/**
 * The rail with the marks for what the attorney has finished, on every route of
 * a draft: the invention's settled stamp, the thesis record that names the
 * thesis as it now reads (both the Novelty page and the Thesis stage), and the
 * claim set's lock. A mark stays after the attorney moves on, which is what makes
 * it worth drawing. The reads are the ones the stage pages fetch anyway.
 */
function ConnectedStageRail({
  taskId,
  activeStageId,
  activeSubStepId,
  children,
}: {
  taskId: string;
  activeStageId?: V2StageId;
  activeSubStepId?: SubStepId;
  children?: ReactNode;
}) {
  const { data: invention } = useInventionElements(taskId);
  const { data: gate } = useNoveltyGateFor(taskId);
  const { data: claimStrategy } = useClaimStrategy(taskId);
  const { data: stageGate } = useStageGate(taskId);
  const { data: changes } = useNotifications(taskId);
  const { data: asked } = useQuestions(taskId);
  // The draft's step says where its run stands, read while Generate is open.
  const { data: runState } = useLatestRunState(
    taskId,
    activeStageId === "generate",
  );
  // The thesis is done once every position is settled, as the commit reads it.
  const settled = gate?.commit.ready ?? false;
  return (
    <V2StageRail
      taskId={taskId}
      activeStageId={activeStageId}
      activeSubStepId={activeSubStepId}
      done={{
        invention: invention?.gate.settled ?? false,
        novelty: settled,
        thesis: settled,
        "claim-strategy": claimStrategy?.gate.locked ?? false,
      }}
      locked={shutStages(stageGate)}
      labels={runState === undefined ? {} : { ready: draftStepLabel(runState) }}
      counts={railCounts(changes)}
      questions={{
        asked: (asked?.questions.length ?? 0) > 0,
        open: (asked?.questions ?? []).filter(unresolved).length,
      }}
    >
      {children}
    </V2StageRail>
  );
}

/** Intake's pages: the materials, then the CPC codes confirmed from them. */
function intakeRoutes() {
  return (
    <Route path="intake">
      {/* A bare Intake URL lands on its first page, which is where every
          new draft opens. */}
      <Route index element={<Navigate to="materials" replace />} />
      <Route path="materials" element={<IntakeStagePane />} />
      <Route path="classification" element={<ClassificationStagePane />} />
      <Route path="*" element={<Navigate to="../materials" replace />} />
    </Route>
  );
}

/**
 * The stage routes hosted by the shell.
 *
 * Exported as an element block rather than inlined in `App.tsx` so the layout's
 * own test drives the real route table instead of a copy of it.
 */
export function v2StageRoutes() {
  return (
    <Fragment>
      {/* No draft is open at the root, so the attorney starts from their clients. */}
      <Route index element={<Navigate to={CLIENTS_PATH} replace />} />
      <Route path="drafts/:taskId" element={<KeyedV2Draft />}>
        {/* Nothing POSTs /tasks merely because a route mounted, so a bare
            draft URL redirects rather than creating anything. */}
        <Route index element={<DraftOpening />} />
        {intakeRoutes()}
        {/* Every question still to answer and every one resolved, reached from the rail. */}
        <Route path="questions" element={<OpenQuestionsPage />} />
        <Route path="thesis">
          {/* A bare thesis URL lands on its first page rather than on a chooser. */}
          <Route index element={<Navigate to="invention" replace />} />
          <Route path="invention" element={<InventionStagePane />} />
          {/* Shut while the invention is not confirmed: the guard sends the
              attorney back to Invention off the served gate. */}
          <Route path="novelty" element={<NoveltyGuard />}>
            {/* A bare novelty URL lands on the first tab, and an unknown one on
                the analysis tab, where the footers onto the others live, rather
                than beside nothing. The analysis tab sends a matter never run
                back to the first tab itself, off the served state, which the
                route table cannot know, and the guard over the third tab reads
                the served gate the same way. */}
            <Route index element={<Navigate to="positions" replace />} />
            <Route path="positions" element={<DefinePositionsPane />} />
            <Route path="prior-art" element={<NoveltyPriorArtPane />} />
            <Route path="analysis" element={<NoveltyAnalysisPane />} />
            <Route path="*" element={<Navigate to="../analysis" replace />} />
          </Route>
          <Route path="*" element={<Navigate to="../invention" replace />} />
        </Route>
        {/* Novelty was a stage of its own before it became a page of the thesis.
            Links to it exist, so the old URL still resolves. */}
        <Route
          path="novelty"
          element={<Navigate to="../thesis/novelty" replace />}
        />
        {/* Gated stages: the locked page in their place until the server says open. */}
        <Route
          path="claim-strategy"
          element={<StageGateGuard stage="claim-strategy" />}
        >
          {/* Shut while a position is not settled and no set is stored: the guard
              sends the attorney to the analysis off the served stage. */}
          <Route element={<ClaimStrategyGuard />}>
            <Route index element={<ClaimStrategyStagePane />} />
          </Route>
        </Route>
        <Route path="figures">
          {/* A bare figures URL lands on the first step rather than on a chooser
              for steps that are not built. */}
          <Route index element={<Navigate to="upload" replace />} />
          <Route path="upload" element={<FiguresStagePane />} />
          <Route path="define" element={<FiguresDefinePane />} />
          <Route path="review-insert" element={<FiguresReviewPane />} />
          {/* A typed or bookmarked URL can name a step that no longer exists,
              and with no route behind it the shell renders against an empty
              pane. */}
          <Route path="*" element={<Navigate to="../upload" replace />} />
        </Route>
        <Route
          path="planning-outline"
          element={<StageGateGuard stage="planning-outline" />}
        >
          <Route index element={<PlanningOutlineStagePane />} />
        </Route>
        {/* Generate's pages were once addressed under claim-drafting. */}
        <Route path="claim-drafting/:step?" element={<OldGenerateAddress />} />
        <Route path="generate" element={<StageGateGuard stage="generate" />}>
          {/* A bare Generate URL lands on the review, where a run is started. */}
          <Route index element={<Navigate to="review" replace />} />
          <Route path="review" element={<GenerateReviewPane />} />
          <Route path="ready" element={<GenerateReadyPane />} />
          <Route path="*" element={<Navigate to="../review" replace />} />
        </Route>
      </Route>
    </Fragment>
  );
}

/** An old Generate address opens the same page under generate, keeping its step. */
function OldGenerateAddress() {
  const { step } = useParams();
  return <Navigate to={`../generate/${step ?? ""}`} replace />;
}

/**
 * What the URL names: the stage, the step of a stepped stage, and the tab of a
 * page walked in tabs. Read off route matches rather than params, since the
 * stage is a static segment and an unknown segment resolves to no stage.
 */
function useActiveRoute() {
  const stageMatch = useMatch("/drafts/:taskId/:stageId");
  // A stepped stage matches two segments, so the one-segment match above finds
  // nothing on those routes and the stage comes off this one instead. The splat
  // lets a page walked in tabs, three segments deep, still name its step.
  const subStepMatch = useMatch("/drafts/:taskId/:stageId/:subStepId/*");
  const tabMatch = useMatch("/drafts/:taskId/:stageId/:subStepId/:tabId");
  const activeStage = findStage(
    stageMatch?.params.stageId ?? subStepMatch?.params.stageId,
  );
  const activeSubStep: SubStep | undefined = subStepsOf(
    activeStage?.id,
  )?.steps.find((step) => step.id === subStepMatch?.params.subStepId);
  const figureSubStep =
    activeStage?.id === "figures" ? activeSubStep : undefined;
  const activeTab = activeSubStep?.tabs?.tabs.find(
    (tab) => tab.id === tabMatch?.params.tabId,
  );
  return { activeStage, activeSubStep, figureSubStep, activeTab };
}

/**
 * The column the pages render in: the strip of steps or tabs above them, and
 * the measure they are set to.
 *
 * It publishes its own geometry as custom properties, so a pane pinning
 * something to the viewport reads what sits above it rather than
 * reconstructing it from the shell's constants.
 */
function ContentColumn({
  taskId,
  activeStage,
  activeSubStep,
  figureSubStep,
  activeTab,
  stripShown,
}: {
  taskId: string | undefined;
  activeStage: ReturnType<typeof useActiveRoute>["activeStage"];
  activeSubStep: ReturnType<typeof useActiveRoute>["activeSubStep"];
  figureSubStep: ReturnType<typeof useActiveRoute>["figureSubStep"];
  activeTab: ReturnType<typeof useActiveRoute>["activeTab"];
  stripShown: boolean;
}) {
  return (
    <main
      // A pinned footer covers the foot of the page. Scroll padding keeps whatever is scrolled
      // to, or focused, above it, so a row's controls never sit under the bar unseen. `relative`
      // makes the column the box of what a page places outside the flow (a hidden label), so the
      // column clips and scrolls it rather than the document growing past the screen.
      className="relative min-h-0 overflow-y-auto px-8 [&:has([data-pinned])]:scroll-pb-44"
      style={
        {
          "--stage-top": `${String(SHELL_HEADER_HEIGHT)}px`,
          "--stage-tabs-h": `${String(stripShown ? STAGE_TABS_HEIGHT : 0)}px`,
        } as CSSProperties
      }
    >
      {figureSubStep !== undefined && taskId !== undefined && (
        <ConnectedFigureStageTabs
          taskId={taskId}
          activeId={figureSubStep.id as FigureSubStepId}
        />
      )}
      {activeTab !== undefined &&
        activeSubStep?.tabs !== undefined &&
        activeStage !== undefined &&
        taskId !== undefined &&
        // Novelty is the one page whose tabs a served gate narrows. Any
        // other tabbed page draws its strip directly and fetches nothing.
        (activeSubStep.id === "novelty" ? (
          <ConnectedNoveltyTabs
            taskId={taskId}
            stageId={activeStage.id}
            step={activeSubStep}
            group={activeSubStep.tabs}
            active={activeTab}
          />
        ) : (
          <PageTabs
            taskId={taskId}
            stageId={activeStage.id}
            step={activeSubStep}
            group={activeSubStep.tabs}
            active={activeTab}
          />
        ))}
      {/* The widest page's frame, so a pane's loading line sits where its page
          will; the page sets its own column inside it (StagePage). */}
      <div className="mx-auto max-w-[1040px]">
        <Outlet />
      </div>
    </main>
  );
}

/**
 * The v2 attorney-workflow shell: a full-viewport grid of stage rail + content,
 * with Legal's notice about AI-generated output held under it.
 *
 * Sibling of `AppLayout`, never nested in it — nesting would put the v1 purple
 * sidebar beside the stage rail. The tracks are one token.
 *
 * The clipping root holds the viewport height and the overflow rule, rather
 * than the grid doing it, so that every stage route is inside the clip.
 * The toast surface has to sit outside the grid or it takes a row of its own,
 * and sonner's wrapper is `position: static`, so as a bare sibling of a
 * full-viewport grid it sat in document flow just below the fold: a toast gave
 * it height, the document outgrew the viewport, and the header and rail
 * scrolled away. Only the content column is meant to scroll.
 *
 * `overflow-hidden` sits here rather than on the shared `body`, where it would
 * leak into every v1 route. It clips only what has its box here or inside, so
 * the root is `relative`: an element placed outside the flow anywhere in the
 * shell is clipped by it, never by the document.
 */
export function V2Layout() {
  const { taskId } = useParams<{ taskId: string }>();
  const { activeStage, activeSubStep, figureSubStep, activeTab } =
    useActiveRoute();
  useRecordLastStage(taskId, activeStage?.id);
  const stripShown = figureSubStep !== undefined || activeTab !== undefined;
  // The Terminology panel is the shell's: its toggle sits in the header and,
  // open, it takes a column of its own beside the content on every page that
  // offers it, so it stays open as the attorney moves between them.
  const terminology = useTerminology(
    taskId,
    terminologyPlace(activeStage?.id, activeSubStep?.id),
    // The page's name for the Return bar: a thesis or Generate page by its own, any other by its stage.
    (activeStage?.id === "thesis" || activeStage?.id === "generate"
      ? activeSubStep?.label
      : activeStage?.label) ?? "",
  );

  return (
    <div data-testid="v2-root" className="relative h-screen overflow-hidden">
      <div
        data-testid="v2-shell"
        className={cn(
          "grid h-full grid-rows-[var(--shell-header-h)_minmax(0,1fr)_auto] bg-background text-foreground",
          terminology.shown
            ? "grid-cols-[224px_1fr] dock:grid-cols-[224px_1fr_375px]"
            : "grid-cols-[224px_1fr]",
        )}
        style={
          {
            "--shell-header-h": `${String(SHELL_HEADER_HEIGHT)}px`,
          } as CSSProperties
        }
      >
        <ConnectedV2Header
          terminology={<TerminologyToggle terminology={terminology} />}
        />
        {taskId !== undefined ? (
          <ConnectedStageRail
            taskId={taskId}
            activeStageId={activeStage?.id}
            activeSubStepId={activeSubStep?.id}
          >
            <V2LegalPanel />
          </ConnectedStageRail>
        ) : (
          <V2StageRail
            activeStageId={activeStage?.id}
            activeSubStepId={activeSubStep?.id}
          >
            <V2LegalPanel />
          </V2StageRail>
        )}
        {/* The content column publishes its own geometry: what sits above it in the
            shell, and whether it is rendering a strip of steps or tabs. A pane that
            pins something to the viewport reads these rather than reconstructing them. */}
        {/* The pages reach the shell's Terminology state: Generate's review lists the terms. */}
        <TerminologyContext value={terminology}>
          <ContentColumn
            taskId={taskId}
            activeStage={activeStage}
            activeSubStep={activeSubStep}
            figureSubStep={figureSubStep}
            activeTab={activeTab}
            stripShown={stripShown}
          />
        </TerminologyContext>
        <TerminologyDock terminology={terminology} />
        <TerminologyTrip terminology={terminology} />
        {/* The shell's last row, from the rail's edge to the window's. Outside
            the scrolling column, so a long draft cannot push it off the bottom. */}
        <ShellFoot />
      </div>
      {/* Outside the grid so it takes no row, inside the clip so it can add no
          document height. Its toasts are `position: fixed`, which no ancestor
          here turns into a containing block, so they still reach the viewport. */}
      <ToastSurface />
    </div>
  );
}
